import React, {useState} from 'react';
import {ContainerNuevo, ContainerOptions, Explanation, InputError, InputOption, InputSend, LabelCart, LabelName} from './cuenta.elements';
import {ContenedorGlobal} from '../../components/Theme/theme.elements';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {Link} from 'react-router-dom';
import {useTenantContext} from '../../tenant/TenantContext';
import {useSignup} from '../application/useSignup';
import {RemoteDataUI} from '../../common/ui/RemoteDataUI';
import {Spinner} from 'react-bootstrap';
import {VerificationCodeStep} from './VerificationCodeStep';
import {campoExtraDe, esDocumentoValido, PROVINCIAS, TIPOS_EMPRESA} from '../domain/registrationOptions';
import {checkRegistrationExists} from '../infrastructure/UserApi';
import {isSuccess} from '../../common/domain/RemoteData.model';

/**
 * Alta de clientes NUEVOS (fase 3): tipo de empresa cerrado con campo condicional, IBAN para la
 * domiciliación, verificación del email con código y aviso si ya es cliente.
 * La solicitud sigue autorizándola la empresa a mano en el admin.
 */
export const Register = () => {
  const { tenantId, name, businessName, privacyPolicy } = useTenantContext();
  const { signupRemoteData, signup } = useSignup();
  const [verified, setVerified] = useState(false);
  const [yaCliente, setYaCliente] = useState({ cif: false, email: false });

  const comprobarExistente = async (campo, valor) => {
    if (!valor) return;
    const remote = await checkRegistrationExists(campo === 'cif' ? { cif: valor } : { email: valor }, tenantId);
    if (isSuccess(remote)) setYaCliente(prev => ({ ...prev, [campo]: !!remote.value?.[campo] }));
  };

  const validationSchema = Yup.object({
    nombre: Yup.string().required('Este campo es obligatorio').min(2, 'El valor del campo es demasiado corto'),
    empresa: Yup.string().required('Este campo es obligatorio').min(2, 'El valor del campo es demasiado corto'),
    email: Yup.string().email('El email no tiene un formato correcto').required('Este campo es obligatorio'),
    telefono: Yup.string()
                 .required('Este campo es obligatorio')
                 .matches(/^(\+\d{1,3})?\s?-?\d{3}\s?-?\d{3}\s?-?\d{3}$/, 'Introduzca un teléfono válido (ej: 123 456 789 o +34 123 456 789)'),
    nifCif: Yup.string()
               .required('Este campo es obligatorio')
               .test('documento', 'El CIF/NIF no tiene un formato válido', (v) => esDocumentoValido(v)),
    tipoEmpresa: Yup.string().oneOf(TIPOS_EMPRESA, 'Elige un tipo de empresa').required('Este campo es obligatorio'),
    numAutorizacion: Yup.string().when('tipoEmpresa', {
      is: (t) => campoExtraDe(t) === 'autorizacion',
      then: (s) => s.required('El número de autorización es obligatorio para este tipo de empresa').min(2, 'El valor del campo es demasiado corto')
    }),
    numColegiado: Yup.string().when('tipoEmpresa', {
      is: (t) => campoExtraDe(t) === 'colegiado',
      then: (s) => s.required('El número de colegiado es obligatorio para este tipo de empresa').min(2, 'El valor del campo es demasiado corto')
    }),
    comunicacionRealizada: Yup.string().when('tipoEmpresa', {
      is: (t) => campoExtraDe(t) === 'comunicacion',
      then: (s) => s.oneOf(['si', 'no'], 'Indica si tienes realizada la comunicación').required('Indica si tienes realizada la comunicación')
    }),
    direccion: Yup.string().required('Este campo es obligatorio').min(2, 'El valor del campo es demasiado corto'),
    poblacion: Yup.string().required('Este campo es obligatorio').min(2, 'El valor del campo es demasiado corto'),
    provincia: Yup.string().oneOf(PROVINCIAS, 'Elige una provincia').required('Este campo es obligatorio'),
    codigoPostal: Yup.string().required('Este campo es obligatorio').matches(/^\d{5}$/, 'El código postal son 5 dígitos'),
    direccionEnvio: Yup.string(),
    cuentaCorriente: Yup.string()
                        .required('Este campo es obligatorio')
                        .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/, 'IBAN no válido'),
    recargoEquivalencia: Yup.string().required('Este campo es obligatorio'),
    facturaElectronica: Yup.boolean(),
    aceptaTerminos: Yup.boolean().oneOf([true], 'Debe aceptar los términos y condiciones para continuar')
  });

  const toRequest = (values) => {
    const extra = campoExtraDe(values.tipoEmpresa);
    return {
      nombre: values.nombre,
      empresa: values.empresa,
      email: values.email,
      telefono: values.telefono,
      nifCif: values.nifCif.toUpperCase().replace(/[\s-]/g, ''),
      tipoEmpresa: values.tipoEmpresa,
      direccion: values.direccion,
      poblacion: `${values.poblacion} (${values.provincia})`,
      codigoPostal: values.codigoPostal,
      direccionEnvio: values.direccionEnvio,
      numColegiado: extra === 'colegiado' ? values.numColegiado : '',
      numAutorizacion: extra === 'autorizacion' ? values.numAutorizacion : '',
      comunicacionRealizada: extra === 'comunicacion' ? values.comunicacionRealizada === 'si' : null,
      cuentaCorriente: values.cuentaCorriente.toUpperCase().replace(/\s/g, ''),
      recargoEquivalencia: Number(values.recargoEquivalencia),
      facturaElectronica: !!values.facturaElectronica,
      aceptaTerminos: !!values.aceptaTerminos
    };
  };

  return (
    <div className="pt-4">
      <ContenedorGlobal className="narrow">
        <ContainerNuevo className="col-md-12 bs coloured" background={'#F0F0F0'}>
          <div className="col-md-12">
            <LabelName>Registro para nuevos clientes</LabelName>
          </div>

          <RemoteDataUI remoteData={signupRemoteData}>
            <RemoteDataUI.Initial>
              <Formik
                initialValues={{
                  nombre: '', empresa: '', email: '', telefono: '', nifCif: '', tipoEmpresa: '',
                  numAutorizacion: '', numColegiado: '', comunicacionRealizada: '',
                  direccion: '', poblacion: '', provincia: '', codigoPostal: '', direccionEnvio: '', cuentaCorriente: '',
                  recargoEquivalencia: '1', facturaElectronica: false, aceptaTerminos: false
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => signup(toRequest(values), tenantId)}>
                {({ handleChange, handleBlur, values, errors, touched }) => {
                  const extra = campoExtraDe(values.tipoEmpresa);
                  return (
                    <Form>
                      <div className="row">
                        <ContainerOptions className="col-md-12">
                          <LabelCart className="mandatory">Nombre y apellidos</LabelCart>
                          <InputOption type="text" id="nombre" value={values.nombre} onChange={handleChange} onBlur={handleBlur} />
                          {errors.nombre && touched.nombre && (<InputError>{errors.nombre}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-12">
                          <LabelCart className="mandatory">Razón social de la empresa</LabelCart>
                          <InputOption type="text" id="empresa" value={values.empresa} onChange={handleChange} onBlur={handleBlur} />
                          {errors.empresa && touched.empresa && (<InputError>{errors.empresa}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-6">
                          <LabelCart className="mandatory">Email</LabelCart>
                          <InputOption type="text" id="email" value={values.email} onChange={handleChange}
                                       onBlur={(e) => { handleBlur(e); comprobarExistente('email', e.target.value); }} />
                          <Explanation>Te enviaremos un código para confirmarlo</Explanation>
                          {errors.email && touched.email && (<InputError>{errors.email}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-6">
                          <LabelCart className="mandatory">Teléfono</LabelCart>
                          <InputOption type="text" id="telefono" value={values.telefono} onChange={handleChange} onBlur={handleBlur} />
                          {errors.telefono && touched.telefono && (<InputError>{errors.telefono}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-6">
                          <LabelCart className="mandatory">CIF / NIF</LabelCart>
                          <InputOption type="text" id="nifCif" value={values.nifCif} onChange={handleChange}
                                       onBlur={(e) => { handleBlur(e); comprobarExistente('cif', e.target.value); }} />
                          {errors.nifCif && touched.nifCif && (<InputError>{errors.nifCif}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-6">
                          <LabelCart className="mandatory">Tipo de empresa</LabelCart>
                          <select id="tipoEmpresa" name="tipoEmpresa" value={values.tipoEmpresa} onChange={handleChange} onBlur={handleBlur}>
                            <option value="">Elige una opción</option>
                            {TIPOS_EMPRESA.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          {errors.tipoEmpresa && touched.tipoEmpresa && (<InputError>{errors.tipoEmpresa}</InputError>)}
                        </ContainerOptions>

                        {(yaCliente.cif || yaCliente.email) && (
                          <div className="col-md-12">
                            <div className="alert alert-warning">
                              Parece que ya eres cliente de {name}: {yaCliente.cif ? 'ese CIF/NIF' : 'ese email'} consta en nuestros
                              registros. Si ya tienes número de cliente, usa <Link to="/registroclientes">Soy cliente, solicitar acceso web</Link>.
                            </div>
                          </div>
                        )}

                        {extra === 'autorizacion' && (
                          <ContainerOptions className="col-md-6">
                            <LabelCart className="mandatory">Número de autorización</LabelCart>
                            <InputOption type="text" id="numAutorizacion" value={values.numAutorizacion} onChange={handleChange} onBlur={handleBlur} />
                            <Explanation>Autorización sanitaria de {values.tipoEmpresa.toLowerCase()}</Explanation>
                            {errors.numAutorizacion && touched.numAutorizacion && (<InputError>{errors.numAutorizacion}</InputError>)}
                          </ContainerOptions>
                        )}

                        {extra === 'colegiado' && (
                          <ContainerOptions className="col-md-6">
                            <LabelCart className="mandatory">Número de colegiado</LabelCart>
                            <InputOption type="text" id="numColegiado" value={values.numColegiado} onChange={handleChange} onBlur={handleBlur} />
                            {errors.numColegiado && touched.numColegiado && (<InputError>{errors.numColegiado}</InputError>)}
                          </ContainerOptions>
                        )}

                        {extra === 'comunicacion' && (
                          <ContainerOptions className="col-md-6">
                            <LabelCart className="mandatory">¿Tienes realizada la comunicación?</LabelCart>
                            <select id="comunicacionRealizada" name="comunicacionRealizada" value={values.comunicacionRealizada} onChange={handleChange} onBlur={handleBlur}>
                              <option value="">Elige una opción</option>
                              <option value="si">Sí</option>
                              <option value="no">No</option>
                            </select>
                            {errors.comunicacionRealizada && touched.comunicacionRealizada && (<InputError>{errors.comunicacionRealizada}</InputError>)}
                          </ContainerOptions>
                        )}

                        <ContainerOptions className="col-md-12">
                          <LabelCart className="mandatory">Dirección</LabelCart>
                          <InputOption type="text" id="direccion" value={values.direccion} onChange={handleChange} onBlur={handleBlur} />
                          {errors.direccion && touched.direccion && (<InputError>{errors.direccion}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-5">
                          <LabelCart className="mandatory">Población</LabelCart>
                          <InputOption type="text" id="poblacion" value={values.poblacion} onChange={handleChange} onBlur={handleBlur} />
                          {errors.poblacion && touched.poblacion && (<InputError>{errors.poblacion}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-4">
                          <LabelCart className="mandatory">Provincia</LabelCart>
                          <select id="provincia" name="provincia" value={values.provincia} onChange={handleChange} onBlur={handleBlur}>
                            <option value="">Elige una provincia</option>
                            {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                          {errors.provincia && touched.provincia && (<InputError>{errors.provincia}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-3">
                          <LabelCart className="mandatory">CP</LabelCart>
                          <InputOption type="text" id="codigoPostal" inputMode="numeric" maxLength={5} value={values.codigoPostal} onChange={handleChange} onBlur={handleBlur} />
                          {errors.codigoPostal && touched.codigoPostal && (<InputError>{errors.codigoPostal}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-12">
                          <LabelCart>Dirección de envío</LabelCart>
                          <InputOption type="text" id="direccionEnvio" value={values.direccionEnvio} onChange={handleChange} onBlur={handleBlur} />
                          <Explanation>Sólo si es distinta a la dirección de la empresa</Explanation>
                        </ContainerOptions>

                        <ContainerOptions className="col-md-6">
                          <LabelCart className="mandatory">IBAN para domiciliación SEPA</LabelCart>
                          <InputOption type="text" id="cuentaCorriente" value={values.cuentaCorriente} onChange={handleChange} onBlur={handleBlur} />
                          <Explanation>Sin espacios ni símbolos de separación</Explanation>
                          {errors.cuentaCorriente && touched.cuentaCorriente && (<InputError>{errors.cuentaCorriente}</InputError>)}
                        </ContainerOptions>

                        <ContainerOptions className="col-md-12">
                          <LabelCart>Recargo de equivalencia (IVA DEL PRODUCTO + RECARGO)</LabelCart>
                          <select id="recargoEquivalencia" name="recargoEquivalencia" value={values.recargoEquivalencia} onChange={handleChange} onBlur={handleBlur}>
                            <option value="1">Sin recargo de equivalencia</option>
                            <option value="2">Con recargo de equivalencia</option>
                            <option value="3">Ambas opciones</option>
                          </select>
                        </ContainerOptions>

                        <ContainerOptions className="col-md-12 mt-4">
                          <input type="checkbox" id="facturaElectronica" checked={values.facturaElectronica} onChange={handleChange} onBlur={handleBlur} />
                          <label htmlFor="facturaElectronica">Quiero factura electrónica</label>
                        </ContainerOptions>

                        <ContainerOptions className="col-md-12">
                          <input type="checkbox" id="aceptaTerminos" checked={values.aceptaTerminos} onChange={handleChange} onBlur={handleBlur} />
                          <label htmlFor="aceptaTerminos">
                            Acepto la LOPD, los <a href={privacyPolicy} target="_blank" rel="noreferrer">términos y condiciones de {businessName}</a>
                          </label>
                          {errors.aceptaTerminos && touched.aceptaTerminos && (<InputError>{errors.aceptaTerminos}</InputError>)}
                        </ContainerOptions>
                      </div>
                      <div className="mt-4 explanation">
                        <p>Al enviar recibirás un código por email para confirmar tu dirección. Después nuestro personal revisará la solicitud; no es un proceso automatizado y podemos tardar hasta 48 horas. Gracias por confiar en {name}.</p>
                      </div>

                      <div className="d-flex flex-column flex-md-row justify-content-center justify-content-md-between align-items-md-end gap-2 submit-row">
                        <InputSend type="submit" value="Continuar registro" />
                        <div className="order-md-first text-center">
                          <Link to="/iniciar-sesion">Volver a inicio de sesión</Link>
                        </div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </RemoteDataUI.Initial>

            <RemoteDataUI.Pending>
              <div className="d-flex justify-content-center"><Spinner /></div>
            </RemoteDataUI.Pending>

            <RemoteDataUI.Success>
              {(registration) => verified ? (
                <div className="successMessage text-success">
                  <p>Email confirmado. Hemos recibido tu solicitud y contactaremos muy pronto para confirmar el registro.</p>
                  <p>Gracias por tu interés en {name}</p>
                </div>
              ) : (
                <VerificationCodeStep registration={registration} tenantId={tenantId} onVerified={() => setVerified(true)} />
              )}
            </RemoteDataUI.Success>

            <RemoteDataUI.Failure>
              <div className="errorMessage">
                <p>Ha ocurrido un error. Por favor, reintente más tarde o contacte con nosotros</p>
                <p>{signupRemoteData.error?.message ?? signupRemoteData.error}</p>
              </div>
            </RemoteDataUI.Failure>
          </RemoteDataUI>
        </ContainerNuevo>
      </ContenedorGlobal>
    </div>
  );
};
